import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type IUser } from 'entities/Users'
import {
  type IBroadcastAssigneeTypes,
  type IParamsBroadcastAssignee,
} from 'entities/Broadcast/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type SendFromStore } from 'widgets/SendFrom'
import { type IBroadcastAssigneeCustomItem } from './types'

export const defaultSettingsItem: IBroadcastAssigneeCustomItem = {
  type: 'default',
  id: 'default',
  label: 'Inbox default settings',
  icon: 'settingsLine',
  tooltipText:
    'New conversations will be assigned based on the rules configured in your inbox settings.',
}

export const unassignedItem: IBroadcastAssigneeCustomItem = {
  type: 'unassigned',
  id: 'unassigned',
  label: 'Unassigned',
  icon: 'userLine',
  tooltipText: 'Every member in the inbox will get a notification',
}

export const contactOwnerItem: IBroadcastAssigneeCustomItem = {
  type: 'contact_owner',
  id: 'contact_owner',
  label: 'Contact owner',
  icon: 'userLine',
  tooltipText:
    'New conversations will be assigned to a contact owner. Existing — stay with who they’re already assigned to.',
}

export class BroadcastAssigneeStore {
  private _search = ''
  private _open = false
  private _loading = true
  private _assignedMemberId: number | null | IBroadcastAssigneeTypes = 'default'

  constructor(private _entities: IEntitiesStore, private _sendFromStore: SendFromStore) {
    makeAutoObservable(this)
  }

  private get _withContactOwner() {
    return this._sendFromStore.paramsSendFrom?.type === 'from_contact_owner'
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await Promise.all([this._entities.inboxesStore.fetchSmartInboxes({ cache: true })])

    runInAction(() => {
      this._loading = false
    })
  }

  onToggle = (value: boolean) => {
    this.setSearch('')
    this.setOpen(value)
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setOpen = (value: boolean) => {
    this._open = value
  }

  setAssignMemberId = (value: number | IBroadcastAssigneeTypes | null) => {
    this._assignedMemberId = value
  }

  get loading() {
    return this._loading
  }

  get open() {
    return this._open
  }

  get search() {
    return this._search
  }

  get assignedMemberId() {
    return this._assignedMemberId
  }

  get inbox() {
    return this._sendFromStore.inbox
  }

  get assignedMember() {
    if (typeof this.assignedMemberId === 'number') {
      return this._entities.usersStore.getItem(this.assignedMemberId)
    }
  }

  get assignParams(): IParamsBroadcastAssignee {
    if (this.assignedMember) {
      return {
        type: 'member',
        member_id: this._assignedMemberId as number,
      }
    }

    return {
      type: this._assignedMemberId as IBroadcastAssigneeTypes,
      member_id: null,
    }
  }

  get assignedCustom(): IBroadcastAssigneeCustomItem | null {
    switch (this.assignedMemberId) {
      case 'contact_owner':
        return contactOwnerItem
      case 'default':
        return defaultSettingsItem
      case 'unassigned':
        return unassignedItem
      default:
        return null
    }
  }

  get members() {
    if (this.inbox?.type !== 'inbox') return []

    const memberIds = this.inbox.memberIds.filter(
      (item) => item !== this._entities.usersStore.user_owner_id
    )
    const ownerContact = this._entities.usersStore.getItem(
      this._entities.usersStore.user_owner_id
    ) as IUser

    const members = memberIds
      .reduce<IUser[]>((state, id) => {
        const item = this._entities.usersStore.getItem(id)

        return item ? [...state, item] : [...state]
      }, [])
      .filter((member) => {
        if (member.isViewOnlyRole || member.isAway) return false
        return true
      })

    if (!ownerContact.isAway) members.unshift(ownerContact)

    return members
  }

  get customItems(): IBroadcastAssigneeCustomItem[] {
    const customItems = [defaultSettingsItem, unassignedItem]
    if (this._withContactOwner) customItems.push(contactOwnerItem)
    return customItems
  }

  get filteredCustomItems() {
    if (!this._search) return this.customItems

    return this.customItems.filter((item) =>
      item.label.toLowerCase().includes(this._search.toLowerCase())
    )
  }

  get filteredMemberItems() {
    if (!this._search) return this.members

    return this.members.filter((member) =>
      member.name.toLowerCase().includes(this._search.toLowerCase())
    )
  }

  private _disposeSendFromStoreInboxId: IReactionDisposer | null = null
  reactionSendFromStoreInboxId = () => {
    this._disposeSendFromStoreInboxId?.()
    this._disposeSendFromStoreInboxId = reaction(
      () => this._sendFromStore.inbox_id,
      () => {
        this.setAssignMemberId('default')
      }
    )
  }

  dispose = () => {
    this._disposeSendFromStoreInboxId?.()
  }
}
